import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderOpen, TrendingUp, CheckCircle, AlertTriangle,
  Plus, ArrowRight, MapPin, Clock, ChevronRight,
  BarChart2, Activity, Shield
} from 'lucide-react';
import { getStats, getDossiers } from '../api/dossiers';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
dayjs.locale('fr');

/* ─── PALETTE ─── */
const C = {
  sand:      '#f5ede0',
  sandDark:  '#e8d8c0',
  terra:     '#c2521a',
  terraLt:   '#e0784a',
  terraDk:   '#8a3510',
  forest:    '#1a3d28',
  forestMid: '#244d35',
  forestLt:  '#2f6645',
  gold:      '#c8902a',
  goldLt:    '#e8b84a',
  ink:       '#0e1a10',
  mist:      '#6e8070',
  cream:     '#faf6ef',
  white:     '#ffffff',
};

/* ─── STATUS CONFIG ─── */
const STATUS_MAP = {
  en_cours:   { label: 'En cours',   bg: 'rgba(200,144,42,.12)',  color: '#a07020', dot: '#c8902a' },
  valide:     { label: 'Validé',     bg: 'rgba(47,102,69,.12)',   color: '#1a5c34', dot: '#2f6645' },
  litige:     { label: 'Litige',     bg: 'rgba(194,82,26,.1)',    color: '#8a3510', dot: '#c2521a' },
  urgent:     { label: 'Urgent',     bg: 'rgba(194,82,26,.15)',   color: '#8a3510', dot: '#c2521a' },
  nouveau:    { label: 'Nouveau',    bg: 'rgba(26,61,40,.08)',    color: '#1a3d28', dot: '#2f6645' },
};
const getStatus = (d) => {
  if (d.urgent) return STATUS_MAP.urgent;
  return STATUS_MAP[d.statut] || STATUS_MAP.nouveau;
};

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'DM Sans', sans-serif; background: #f2ebe0; color: ${C.forest}; }

/* ── PAGE WRAPPER ── */
.dash {
  min-height: 100vh;
  background:
    radial-gradient(ellipse at 80% 0%, rgba(194,82,26,.06) 0%, transparent 50%),
    radial-gradient(ellipse at 0% 80%, rgba(47,102,69,.05) 0%, transparent 50%),
    #f2ebe0;
}

/* ── HEADER ── */
.dash-header {
  padding: 36px 40px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}
.dash-greeting-label {
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .18em;
  text-transform: uppercase;
  color: ${C.terra};
  margin-bottom: 6px;
}
.dash-greeting-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -.035em;
  color: ${C.ink};
}
.dash-greeting-title em { font-style: italic; color: ${C.terra}; }
.dash-greeting-sub {
  margin-top: 6px;
  font-size: .88rem;
  color: ${C.mist};
}
.btn-new {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 13px 22px;
  border-radius: 13px;
  background: ${C.forest};
  color: ${C.sand};
  font: 600 .9rem 'DM Sans', sans-serif;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(26,61,40,.2);
  transition: transform .25s, box-shadow .25s, background .2s;
  white-space: nowrap;
  flex: none;
  align-self: center;
  text-decoration: none;
  position: relative;
  overflow: hidden;
}
.btn-new::before {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.07), transparent);
  transform: translateX(-100%);
  transition: transform .5s ease;
}
.btn-new:hover::before { transform: translateX(100%); }
.btn-new:hover { transform: translateY(-2px); box-shadow: 0 16px 36px rgba(26,61,40,.28); background: ${C.forestMid}; }

/* ── MAIN CONTENT ── */
.dash-body {
  padding: 32px 40px 48px;
  display: grid;
  gap: 28px;
}

/* ── STAT CARDS ── */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}
@media (max-width: 1100px) { .stats-row { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 600px)  { .stats-row { grid-template-columns: 1fr; } }

.stat-card {
  background: ${C.white};
  border-radius: 22px;
  padding: 24px 22px;
  border: 1px solid rgba(26,61,40,.07);
  box-shadow: 0 4px 18px rgba(26,61,40,.05);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  overflow: hidden;
  cursor: default;
  transition: transform .3s, box-shadow .3s;
}
.stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 36px rgba(26,61,40,.1); }
.stat-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.stat-icon {
  width: 44px; height: 44px;
  border-radius: 13px;
  display: grid; place-items: center;
  flex: none;
}
.stat-badge {
  font-size: .68rem;
  font-weight: 700;
  letter-spacing: .06em;
  padding: 4px 10px;
  border-radius: 999px;
}
.stat-value {
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.2rem, 3.5vw, 3rem);
  font-weight: 700;
  line-height: .95;
  color: ${C.ink};
  letter-spacing: -.03em;
}
.stat-label {
  font-size: .82rem;
  color: ${C.mist};
  margin-top: 4px;
}
/* accent stripe */
.stat-card::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 3px;
  border-radius: 0 0 22px 22px;
  opacity: .5;
}
.stat-card.s-total::after  { background: ${C.forest}; }
.stat-card.s-cours::after  { background: ${C.gold}; }
.stat-card.s-valide::after { background: ${C.forestLt}; }
.stat-card.s-litige::after { background: ${C.terra}; }

/* sparkline fake */
.sparkline {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 28px;
}
.spark-bar {
  flex: 1;
  border-radius: 3px;
  min-height: 4px;
  transition: height .3s ease;
}

/* ── BOTTOM GRID ── */
.bottom-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 22px;
  align-items: start;
}
@media (max-width: 1000px) { .bottom-grid { grid-template-columns: 1fr; } }

/* ── DOSSIERS SECTION ── */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.section-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -.025em;
  color: ${C.ink};
}
.section-title em { font-style: italic; color: ${C.terra}; }
.see-all {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: .82rem;
  font-weight: 600;
  color: ${C.mist};
  text-decoration: none;
  cursor: pointer;
  transition: color .2s;
  border: none;
  background: transparent;
  padding: 0;
}
.see-all:hover { color: ${C.terra}; }

.dossier-list { display: grid; gap: 12px; }

.dossier-card {
  background: ${C.white};
  border-radius: 18px;
  padding: 18px 20px;
  border: 1px solid rgba(26,61,40,.07);
  box-shadow: 0 2px 12px rgba(26,61,40,.04);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: transform .25s, box-shadow .25s, border-color .25s;
  text-decoration: none;
  color: inherit;
}
.dossier-card:hover {
  transform: translateX(4px);
  box-shadow: 0 8px 28px rgba(26,61,40,.1);
  border-color: rgba(194,82,26,.18);
}
.dossier-avatar {
  width: 46px; height: 46px;
  border-radius: 13px;
  background: linear-gradient(135deg, ${C.forest}, ${C.forestLt});
  display: grid; place-items: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${C.sand};
  flex: none;
  letter-spacing: -.02em;
}
.dossier-body { flex: 1; min-width: 0; }
.dossier-ref {
  font-weight: 700;
  font-size: .95rem;
  color: ${C.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dossier-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 5px;
  flex-wrap: wrap;
}
.dossier-meta-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: .78rem;
  color: ${C.mist};
}
.dossier-meta-item svg { flex: none; }
.dossier-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex: none;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .04em;
  white-space: nowrap;
}
.status-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  flex: none;
}
.dossier-date {
  font-size: .75rem;
  color: rgba(110,128,112,.6);
}
.dossier-arrow { color: rgba(110,128,112,.4); }

/* ── SIDE PANEL ── */
.side-panel { display: grid; gap: 18px; }

/* activity card */
.side-card {
  background: ${C.white};
  border-radius: 20px;
  padding: 22px;
  border: 1px solid rgba(26,61,40,.07);
  box-shadow: 0 4px 18px rgba(26,61,40,.05);
}
.side-card-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.ink};
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.side-card-title svg { color: ${C.terra}; }

/* donut */
.donut-wrap {
  display: flex;
  align-items: center;
  gap: 20px;
}
.donut-svg { flex: none; }
.donut-legend { flex: 1; display: grid; gap: 10px; }
.legend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.legend-left { display: flex; align-items: center; gap: 8px; }
.legend-dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.legend-lbl { font-size: .82rem; color: ${C.mist}; }
.legend-val { font-size: .88rem; font-weight: 700; color: ${C.ink}; }

/* countries */
.countries-list { display: grid; gap: 10px; }
.country-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.country-flag { font-size: 1.1rem; flex: none; }
.country-name { font-size: .85rem; font-weight: 500; flex: 1; }
.country-bar-wrap {
  flex: 1;
  height: 5px;
  border-radius: 999px;
  background: rgba(26,61,40,.07);
  overflow: hidden;
}
.country-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, ${C.forestLt}, ${C.gold});
}
.country-pct { font-size: .75rem; font-weight: 700; color: ${C.mist}; width: 32px; text-align: right; }

/* quick actions */
.quick-actions { display: grid; gap: 8px; }
.quick-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid rgba(26,61,40,.09);
  background: rgba(26,61,40,.02);
  cursor: pointer;
  font: 500 .88rem 'DM Sans', sans-serif;
  color: ${C.forest};
  transition: background .2s, border-color .2s, transform .2s;
  text-decoration: none;
}
.quick-btn:hover {
  background: rgba(26,61,40,.05);
  border-color: rgba(26,61,40,.15);
  transform: translateX(3px);
}
.quick-btn-icon {
  width: 34px; height: 34px;
  border-radius: 10px;
  display: grid; place-items: center;
  flex: none;
}
.quick-btn span { flex: 1; }
.quick-btn svg.arrow { color: rgba(110,128,112,.35); }

/* ── LOADING ── */
.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
}
.loader-ring {
  width: 40px; height: 40px;
  border: 3px solid rgba(26,61,40,.1);
  border-top-color: ${C.terra};
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loader-text { font-size: .88rem; color: ${C.mist}; }

/* ── EMPTY ── */
.empty {
  text-align: center;
  padding: 48px 24px;
  color: ${C.mist};
}
.empty-icon { font-size: 3rem; margin-bottom: 12px; }
.empty p { font-size: .9rem; line-height: 1.7; }
`;

/* ─── STAGGER VARIANTS ─── */
const STAGGER = {
  hidden: {},
  visible: { transition: { staggerChildren: .07 } },
};
const ITEM = {
  hidden:  { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: .55, ease: [.22, 1, .36, 1] } },
};

/* ─── DONUT CHART ─── */
function Donut({ segments, size = 100, stroke = 14 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg className="donut-svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(26,61,40,.06)" strokeWidth={stroke} />
      {segments.map(({ pct, color }, i) => {
        const dash = (pct / 100) * circ;
        const el = (
          <motion.circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
            stroke={color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ}`}
            strokeDashoffset={-offset * circ / 100 + circ / 4}
            strokeLinecap="round"
            initial={{ strokeDasharray: `0 ${circ}` }}
            animate={{ strokeDasharray: `${dash} ${circ}` }}
            transition={{ duration: .9, delay: .4 + i * .15, ease: [.22, 1, .36, 1] }}
          />
        );
        offset += pct;
        return el;
      })}
    </svg>
  );
}

/* ─── COUNTER ─── */
function Counter({ to }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const dur = 1200;
    const step = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * to));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [to]);
  return <>{val}</>;
}

/* ─── SPARKLINE ─── */
function Sparkline({ data, color }) {
  return (
    <div className="sparkline">
      {data.map((v, i) => (
        <motion.div key={i} className="spark-bar"
          style={{ background: color, opacity: .15 + (i / data.length) * .65 }}
          initial={{ height: 0 }}
          animate={{ height: `${(v / Math.max(...data)) * 100}%` }}
          transition={{ delay: .5 + i * .04, duration: .4, ease: [.22, 1, .36, 1] }}
        />
      ))}
    </div>
  );
}

/* ─── MAIN ─── */
export default function Dashboard() {
  const navigate = useNavigate();
  const [stats,  setStats]  = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getStats(), getDossiers()])
      .then(([s, d]) => {
        setStats(s.data);
        setRecent(d.data.results || d.data);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="dash">
      <style>{css}</style>
      <div className="loader">
        <div className="loader-ring" />
        <span className="loader-text">Chargement du tableau de bord…</span>
      </div>
    </div>
  );

  const total   = stats?.total    || 0;
  const enCours = stats?.en_cours || 0;
  const valides = stats?.valides  || 0;
  const litiges = stats?.litiges  || 0;

  const donutSegments = [
    { pct: total ? Math.round(enCours / total * 100) : 0, color: C.gold,      label: 'En cours',  val: enCours },
    { pct: total ? Math.round(valides / total * 100) : 0, color: C.forestLt,  label: 'Validés',   val: valides },
    { pct: total ? Math.round(litiges / total * 100) : 0, color: C.terra,     label: 'Litiges',   val: litiges },
  ];

  const STAT_CARDS = [
    { key:'s-total',  icon:<FolderOpen size={20} />,     label:'Total dossiers', value: total,   sparkData:[4,7,5,9,8,12,10,14,13,17,15,total||18], iconBg:'rgba(26,61,40,.1)',   iconColor: C.forest,    badge:'+12%', badgeBg:'rgba(26,61,40,.08)', badgeColor: C.forestMid },
    { key:'s-cours',  icon:<TrendingUp size={20} />,     label:'En cours',       value: enCours, sparkData:[2,3,5,4,6,5,7,8,6,9,8,enCours||9],   iconBg:'rgba(200,144,42,.12)', iconColor: C.gold,      badge:'Actif', badgeBg:'rgba(200,144,42,.1)', badgeColor: C.gold },
    { key:'s-valide', icon:<CheckCircle size={20} />,    label:'Validés',        value: valides, sparkData:[1,2,3,4,4,5,6,7,8,9,10,valides||11],  iconBg:'rgba(47,102,69,.12)',  iconColor: C.forestLt,  badge:'OK',  badgeBg:'rgba(47,102,69,.1)', badgeColor: C.forestLt },
    { key:'s-litige', icon:<AlertTriangle size={20} />,  label:'Litiges',        value: litiges, sparkData:[0,1,0,1,2,1,2,2,3,2,3,litiges||3],   iconBg:'rgba(194,82,26,.1)',   iconColor: C.terra,     badge:'⚠', badgeBg:'rgba(194,82,26,.08)', badgeColor: C.terraDk },
  ];

  const COUNTRIES = [
    { flag:'🇹🇬', name:'Togo',          pct: 34 },
    { flag:'🇧🇯', name:'Bénin',         pct: 24 },
    { flag:'🇸🇳', name:'Sénégal',       pct: 18 },
    { flag:'🇨🇮', name:"Côte d'Ivoire", pct: 14 },
    { flag:'🇬🇭', name:'Ghana',         pct: 10 },
  ];

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

  return (
    <div className="dash">
      <style>{css}</style>

      {/* ── HEADER ── */}
      <motion.div className="dash-header"
        initial={{ opacity:0, y:-16 }} animate={{ opacity:1, y:0 }}
        transition={{ duration:.6, ease:[.22,1,.36,1] }}>
        <div>
          <div className="dash-greeting-label">Tableau de bord</div>
          <h1 className="dash-greeting-title">
            {greeting}, <em>bienvenue</em>
          </h1>
          <p className="dash-greeting-sub">
            {dayjs().format('dddd D MMMM YYYY')} — {total} dossier{total > 1 ? 's' : ''} au total
          </p>
        </div>
        <button className="btn-new" onClick={() => navigate('/dossiers/nouveau')}>
          <Plus size={16} /> Nouveau dossier
        </button>
      </motion.div>

      <div className="dash-body">

        {/* ── STAT CARDS ── */}
        <motion.div className="stats-row" variants={STAGGER} initial="hidden" animate="visible">
          {STAT_CARDS.map(({ key, icon, label, value, sparkData, iconBg, iconColor, badge, badgeBg, badgeColor }) => (
            <motion.div key={key} variants={ITEM} className={`stat-card ${key}`}>
              <div className="stat-card-top">
                <div className="stat-icon" style={{ background: iconBg, color: iconColor }}>
                  {icon}
                </div>
                <span className="stat-badge" style={{ background: badgeBg, color: badgeColor }}>
                  {badge}
                </span>
              </div>
              <div>
                <div className="stat-value"><Counter to={value} /></div>
                <div className="stat-label">{label}</div>
              </div>
              <Sparkline data={sparkData} color={iconColor} />
            </motion.div>
          ))}
        </motion.div>

        {/* ── BOTTOM GRID ── */}
        <div className="bottom-grid">

          {/* dossiers récents */}
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:.25, duration:.6, ease:[.22,1,.36,1] }}>
            <div className="section-header">
              <h2 className="section-title">Dossiers <em>récents</em></h2>
              <button className="see-all" onClick={() => navigate('/dossiers')}>
                Tous les dossiers <ChevronRight size={15} />
              </button>
            </div>

            <div className="dossier-list">
              <AnimatePresence>
                {recent.length === 0 ? (
                  <div className="empty">
                    <div className="empty-icon">📂</div>
                    <p>Aucun dossier pour l'instant.<br />Commencez par créer votre première vérification.</p>
                  </div>
                ) : (
                  recent.slice(0, 6).map((d, i) => {
                    const st = getStatus(d);
                    const initials = (d.reference || 'TT').replace(/[^A-Z0-9]/gi, '').slice(0,2).toUpperCase();
                    return (
                      <motion.div key={d.id}
                        initial={{ opacity:0, x:-14 }}
                        animate={{ opacity:1, x:0 }}
                        transition={{ delay: .3 + i * .07, duration:.5, ease:[.22,1,.36,1] }}
                        className="dossier-card"
                        onClick={() => navigate(`/dossiers/${d.id}`)}>
                        <div className="dossier-avatar">{initials}</div>
                        <div className="dossier-body">
                          <div className="dossier-ref">{d.reference}</div>
                          <div className="dossier-meta">
                            <span className="dossier-meta-item">
                              <MapPin size={11} /> {d.localisation}
                            </span>
                            <span className="dossier-meta-item">
                              <Clock size={11} /> {dayjs(d.created_at).format('DD MMM YYYY')}
                            </span>
                            {d.mandataire_nom && (
                              <span className="dossier-meta-item">
                                {d.mandataire_nom}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="dossier-right">
                          <span className="status-chip" style={{ background: st.bg, color: st.color }}>
                            <span className="status-dot" style={{ background: st.dot }} />
                            {st.label}
                          </span>
                          <span className="dossier-date">{dayjs(d.created_at).fromNow?.() || dayjs(d.created_at).format('DD/MM/YYYY')}</span>
                        </div>
                        <ChevronRight size={16} className="dossier-arrow" />
                      </motion.div>
                    );
                  })
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* side panel */}
          <motion.div className="side-panel"
            initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }}
            transition={{ delay:.35, duration:.6, ease:[.22,1,.36,1] }}>

            {/* répartition donut */}
            <div className="side-card">
              <div className="side-card-title">
                Répartition <BarChart2 size={16} />
              </div>
              <div className="donut-wrap">
                <Donut segments={donutSegments} size={96} stroke={13} />
                <div className="donut-legend">
                  {donutSegments.map(({ label, val, color, pct }) => (
                    <div key={label} className="legend-item">
                      <div className="legend-left">
                        <div className="legend-dot" style={{ background: color }} />
                        <span className="legend-lbl">{label}</span>
                      </div>
                      <span className="legend-val">{val} <span style={{ opacity:.4, fontWeight:400, fontSize:'.75rem' }}>({pct}%)</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* pays */}
            <div className="side-card">
              <div className="side-card-title">
                Pays couverts <Activity size={16} />
              </div>
              <div className="countries-list">
                {COUNTRIES.map(({ flag, name, pct }) => (
                  <div key={name} className="country-row">
                    <span className="country-flag">{flag}</span>
                    <span className="country-name">{name}</span>
                    <div className="country-bar-wrap">
                      <motion.div className="country-bar-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ delay: .6, duration: .8, ease: [.22, 1, .36, 1] }}
                      />
                    </div>
                    <span className="country-pct">{pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* actions rapides */}
            <div className="side-card">
              <div className="side-card-title">Actions rapides <Shield size={16} /></div>
              <div className="quick-actions">
                {[
                  { icon:'📂', label:'Tous les dossiers',  path:'/dossiers',         bg:'rgba(26,61,40,.07)',    color: C.forest },
                  { icon:'➕', label:'Nouveau dossier',    path:'/dossiers/nouveau', bg:'rgba(194,82,26,.08)',   color: C.terraDk },
                  { icon:'📊', label:'Rapports',           path:'/rapports',         bg:'rgba(200,144,42,.08)',  color: '#7a5a10' },
                  { icon:'⚙️', label:'Paramètres',         path:'/settings',         bg:'rgba(26,61,40,.05)',    color: C.mist },
                ].map(({ icon, label, path, bg, color }) => (
                  <button key={label} className="quick-btn" onClick={() => navigate(path)}>
                    <div className="quick-btn-icon" style={{ background: bg, color }}>{icon}</div>
                    <span>{label}</span>
                    <ArrowRight size={14} className="arrow" />
                  </button>
                ))}
              </div>
            </div>

          </motion.div>
        </div>
      </div>
    </div>
  );
}